// src/tabs/GessoSubTab.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, Square, RefreshCw, Clock, Beaker, Save, ListTodo, AlertCircle, Plus, Trash2 } from "lucide-react";
import { pb } from "../services/pocketbase";

export default function GessoSubTab({ user }) {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState("");
  const [qtyGesso, setQtyGesso] = useState(() => localStorage.getItem("gesso_qty") || "500");
  const [qtyAgua, setQtyAgua] = useState(0);
  const [qtyTotal, setQtyTotal] = useState(0);

  // States for Custom Recipe Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  const [newRecipeName, setNewRecipeName] = useState("");
  const [newPropGesso, setNewPropGesso] = useState("100");
  const [newPropAgua, setNewPropAgua] = useState("38");
  const [newObs, setNewObs] = useState("");
  const [newSteps, setNewSteps] = useState([
    { id: "1", nome: "Mistura manual", minutos: 1, segundos: 0, ordem: 1 }
  ]);

  // Timer states
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [timerStatus, setTimerStatus] = useState("parado"); // "parado", "rodando", "pausado", "concluido"
  const [totalSecondsElapsed, setTotalSecondsElapsed] = useState(0);

  const timerRef = useRef(null);

  // Load recipes from PB
  const loadRecipes = useCallback(async () => {
    try {
      const records = await pb.collection("gessos_receitas").getFullList({ sort: "nome" });
      setRecipes(records);
      if (records.length > 0) {
        const lastSelected = localStorage.getItem("gesso_recipe_id");
        if (lastSelected && records.some(r => r.id === lastSelected)) {
          setSelectedRecipeId(lastSelected);
        } else {
          setSelectedRecipeId(records[0].id);
        }
      }
    } catch (e) {
      console.error("Erro loading gesso recipes:", e);
    }
  }, []);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  // Debounced input persistence
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem("gesso_qty", qtyGesso);
    }, 1200);
    return () => clearTimeout(timer);
  }, [qtyGesso]);

  useEffect(() => {
    if (selectedRecipeId) {
      localStorage.setItem("gesso_recipe_id", selectedRecipeId);
    }
  }, [selectedRecipeId]);

  // Selected recipe
  const activeRecipe = recipes.find(r => r.id === selectedRecipeId);

  // Calculate values
  useEffect(() => {
    if (activeRecipe) {
      const gesso = parseFloat(qtyGesso) || 0;
      const propGesso = activeRecipe.prop_gesso || 100;
      const propAgua = activeRecipe.prop_agua || 38;
      const agua = (gesso / propGesso) * propAgua;
      setQtyAgua(Math.round(agua * 10) / 10);
      setQtyTotal(Math.round((gesso + agua) * 10) / 10);
    }
  }, [qtyGesso, activeRecipe]);

  // Sound generator
  const playBeep = useCallback((times = 1) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      
      let delay = 0;
      for (let i = 0; i < times; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(880, ctx.currentTime + delay);
        
        gain.gain.setValueAtTime(0.15, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.2);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.2);
        delay += 0.3;
      }
    } catch (e) {
      console.error("Erro ao tocar som:", e);
    }
  }, []);

  // Timer runner
  useEffect(() => {
    if (timerStatus === "rodando") {
      timerRef.current = setInterval(() => {
        setSecondsRemaining(prev => {
          if (prev <= 1) {
            // Step finished
            const nextIdx = currentStepIdx + 1;
            const steps = activeRecipe?.etapas || [];
            if (nextIdx < steps.length) {
              playBeep(1);
              setCurrentStepIdx(nextIdx);
              const nextStep = steps[nextIdx];
              return (nextStep.minutos || 0) * 60 + (nextStep.segundos || 0);
            } else {
              // Timer complete
              playBeep(3);
              setTimerStatus("concluido");
              clearInterval(timerRef.current);
              return 0;
            }
          }
          return prev - 1;
        });
        setTotalSecondsElapsed(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerStatus, currentStepIdx, activeRecipe, playBeep]);

  // Controls
  const handlePlay = () => {
    if (timerStatus === "parado" || timerStatus === "concluido") {
      const steps = activeRecipe?.etapas || [];
      if (steps.length === 0) return alert("Esta receita não tem etapas cadastradas.");
      setCurrentStepIdx(0);
      setTotalSecondsElapsed(0);
      const firstStep = steps[0];
      setSecondsRemaining((firstStep.minutos || 0) * 60 + (firstStep.segundos || 0));
      setTimerStatus("rodando");
      playBeep(1);
    } else if (timerStatus === "pausado") {
      setTimerStatus("rodando");
    }
  };

  const handlePause = () => {
    if (timerStatus === "rodando") {
      setTimerStatus("pausado");
    }
  };

  const handleStop = () => {
    setTimerStatus("parado");
    setSecondsRemaining(0);
    setCurrentStepIdx(0);
  };

  const handleReset = () => {
    if (activeRecipe && activeRecipe.etapas && activeRecipe.etapas.length > 0) {
      setCurrentStepIdx(0);
      setTotalSecondsElapsed(0);
      const steps = activeRecipe.etapas;
      const firstStep = steps[0];
      setSecondsRemaining((firstStep.minutos || 0) * 60 + (firstStep.segundos || 0));
      setTimerStatus("parado");
    }
  };

  const saveCalculation = async () => {
    if (!activeRecipe) return;
    try {
      await pb.collection("calculos_gesso").create({
        receita_id: activeRecipe.id,
        receita_nome: activeRecipe.nome,
        qty_gesso: parseFloat(qtyGesso) || 0,
        qty_agua: qtyAgua,
        qty_total: qtyTotal
      });
      alert("Cálculo salvo no histórico com sucesso!");
    } catch (e) {
      alert("Erro ao salvar histórico: " + e.message);
    }
  };

  // Add custom recipe
  const handleAddStep = () => {
    setNewSteps(prev => [
      ...prev,
      { id: Date.now().toString(), nome: "", minutos: 1, segundos: 0, ordem: prev.length + 1 }
    ]);
  };

  const handleRemoveStep = (id) => {
    if (newSteps.length === 1) return;
    setNewSteps(prev => prev.filter(s => s.id !== id).map((s, idx) => ({ ...s, ordem: idx + 1 })));
  };

  const handleStepChange = (id, field, value) => {
    setNewSteps(prev =>
      prev.map(s => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const handleOpenEdit = () => {
    if (!activeRecipe) return;
    setEditingRecipeId(activeRecipe.id);
    setNewRecipeName(activeRecipe.nome);
    setNewPropGesso(activeRecipe.prop_gesso?.toString() || "100");
    setNewPropAgua(activeRecipe.prop_agua?.toString() || "38");
    setNewObs(activeRecipe.observacoes || "");
    setNewSteps(activeRecipe.etapas ? activeRecipe.etapas.map(s => ({ ...s })) : []);
    setIsModalOpen(true);
  };

  const handleDeleteRecipe = async () => {
    if (!selectedRecipeId) return;
    const recipe = recipes.find(r => r.id === selectedRecipeId);
    if (!recipe) return;
    if (!window.confirm(`Deseja realmente excluir a receita de gesso "${recipe.nome}"?`)) return;

    try {
      await pb.collection("gessos_receitas").delete(recipe.id);
      const remaining = recipes.filter(r => r.id !== recipe.id);
      setRecipes(remaining);
      if (remaining.length > 0) {
        setSelectedRecipeId(remaining[0].id);
      } else {
        setSelectedRecipeId("");
      }
      alert("Receita excluída!");
    } catch (err) {
      alert("Erro ao excluir receita: " + err.message);
    }
  };

  const saveCustomRecipe = async (e) => {
    e.preventDefault();
    if (!newRecipeName.trim()) return alert("Digite o nome do gesso.");
    
    // Validar etapas
    for (const step of newSteps) {
      if (!step.nome.trim()) return alert("Preencha o nome de todas as etapas.");
    }

    try {
      const payload = {
        nome: newRecipeName.trim(),
        tipo: editingRecipeId ? activeRecipe.tipo : "customizado",
        prop_agua: Number(newPropAgua),
        prop_gesso: Number(newPropGesso),
        observacoes: newObs,
        etapas: newSteps.map((s, idx) => ({
          id: s.id || (Date.now().toString() + idx),
          nome: s.nome.trim(),
          minutos: Number(s.minutos) || 0,
          segundos: Number(s.segundos) || 0,
          ordem: idx + 1
        }))
      };

      if (editingRecipeId) {
        const record = await pb.collection("gessos_receitas").update(editingRecipeId, payload);
        setRecipes(prev => prev.map(r => r.id === editingRecipeId ? record : r));
        alert("Receita atualizada com sucesso!");
      } else {
        const record = await pb.collection("gessos_receitas").create(payload);
        setRecipes(prev => [...prev, record].sort((a,b) => a.nome.localeCompare(b.nome)));
        setSelectedRecipeId(record.id);
        alert("Receita criada com sucesso!");
      }
      setIsModalOpen(false);
      
      // Reset form
      setEditingRecipeId(null);
      setNewRecipeName("");
      setNewPropGesso("100");
      setNewPropAgua("38");
      setNewObs("");
      setNewSteps([{ id: "1", nome: "Mistura manual", minutos: 1, segundos: 0, ordem: 1 }]);
    } catch (e) {
      alert("Erro ao salvar gesso: " + e.message);
    }
  };

  // Time formatter
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden bg-slate-50">
      
      {/* LEFT: CALCULATOR */}
      <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Beaker className="text-blue-500" /> Calculadora de Gesso
              </h2>
              <p className="text-xs text-slate-500">Mapeamento preciso de proporções para moldes de fundição</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditingRecipeId(null);
                  setNewRecipeName("");
                  setNewPropGesso("100");
                  setNewPropAgua("38");
                  setNewObs("");
                  setNewSteps([{ id: "1", nome: "Mistura manual", minutos: 1, segundos: 0, ordem: 1 }]);
                  setIsModalOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1"
              >
                <Plus size={14} /> Novo Gesso
              </button>
              {activeRecipe && (
                <>
                  <button
                    onClick={handleOpenEdit}
                    className="bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                  >
                    Editar
                  </button>
                  <button
                    onClick={handleDeleteRecipe}
                    className="bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                  >
                    Excluir
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Escolha Gesso */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Escolha o Tipo de Gesso
              </label>
              <select
                value={selectedRecipeId}
                onChange={(e) => setSelectedRecipeId(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-bold bg-white cursor-pointer"
              >
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nome} {r.tipo === "padrao" ? "(Padrão)" : "(Custom)"}
                  </option>
                ))}
              </select>
            </div>

            {/* Input Gesso Qty */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Quantidade de Gesso Desejada (g)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-bold text-lg"
                  value={qtyGesso}
                  onChange={(e) => setQtyGesso(e.target.value)}
                  placeholder="Ex: 500"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-slate-400">g</span>
              </div>
            </div>

            {/* Proporção Debug info */}
            {activeRecipe && (
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200/60 flex items-center justify-between text-xs font-semibold text-slate-600">
                <span>Proporção Água/Gesso:</span>
                <span className="font-bold text-slate-800 font-mono">
                  {activeRecipe.prop_agua}ml / {activeRecipe.prop_gesso}g ({((activeRecipe.prop_agua/activeRecipe.prop_gesso)*100).toFixed(1)}%)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* RESULTS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 shadow-sm text-center">
            <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider block mb-1">Gesso Sólido</span>
            <span className="text-3xl font-extrabold text-blue-900 font-mono">{qtyGesso || 0}g</span>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 shadow-sm text-center">
            <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider block mb-1">Água Necessária</span>
            <span className="text-3xl font-extrabold text-emerald-900 font-mono">{qtyAgua} ml</span>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 shadow-sm text-center">
            <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider block mb-1">Total da Mistura</span>
            <span className="text-3xl font-extrabold text-indigo-900 font-mono">{qtyTotal} g</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={saveCalculation}
            disabled={!activeRecipe}
            className="flex-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 font-bold p-3.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 text-sm disabled:opacity-50"
          >
            <Save size={18} /> Salvar no Histórico
          </button>
        </div>
      </div>

      {/* RIGHT: TIMELINE AND SEQUENTIAL TIMER */}
      <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-slate-200 bg-white flex flex-col shrink-0">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
          <h3 className="font-bold text-slate-700 flex items-center gap-1.5 text-sm">
            <Clock size={16} className="text-blue-500" /> Sequência de Preparo
          </h3>
          {timerStatus !== "parado" && (
            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full animate-pulse">
              Tempo decorrido: {formatTime(totalSecondsElapsed)}
            </span>
          )}
        </div>

        {activeRecipe?.etapas && activeRecipe.etapas.length > 0 ? (
          <>
            {/* TIMER CORE SCREEN */}
            <div className="p-6 border-b border-slate-100 flex flex-col items-center justify-center shrink-0 bg-slate-50/50">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                {timerStatus === "concluido" ? "Concluído!" : `Etapa ${currentStepIdx + 1} de ${activeRecipe.etapas.length}`}
              </span>
              <h4 className="text-lg font-bold text-slate-800 text-center truncate w-full mb-3">
                {timerStatus === "concluido" ? "Processo Finalizado" : activeRecipe.etapas[currentStepIdx]?.nome}
              </h4>

              {/* Regressivo */}
              <div className="text-5xl font-black font-mono text-slate-800 tracking-wider mb-4 drop-shadow-sm">
                {formatTime(secondsRemaining)}
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden mb-6">
                <div
                  className="bg-blue-600 h-full transition-all duration-1000"
                  style={{
                    width: (() => {
                      if (timerStatus === "parado") return "0%";
                      if (timerStatus === "concluido") return "100%";
                      const currentStep = activeRecipe.etapas[currentStepIdx];
                      const totalSecs = (currentStep.minutos || 0) * 60 + (currentStep.segundos || 0);
                      if (totalSecs === 0) return "100%";
                      return `${((totalSecs - secondsRemaining) / totalSecs) * 100}%`;
                    })()
                  }}
                />
              </div>

              {/* Controls Grid */}
              <div className="flex gap-2 w-full">
                {(timerStatus === "parado" || timerStatus === "pausado" || timerStatus === "concluido") && (
                  <button
                    onClick={handlePlay}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold p-2.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 text-xs"
                  >
                    <Play size={14} fill="currentColor" /> Iniciar
                  </button>
                )}
                {timerStatus === "rodando" && (
                  <button
                    onClick={handlePause}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold p-2.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 text-xs"
                  >
                    <Pause size={14} fill="currentColor" /> Pausar
                  </button>
                )}
                {timerStatus !== "parado" && (
                  <button
                    onClick={handleStop}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold p-2.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 text-xs"
                  >
                    <Square size={12} fill="currentColor" /> Parar
                  </button>
                )}
                {timerStatus !== "rodando" && (
                  <button
                    onClick={handleReset}
                    className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded-xl transition-all"
                    title="Reiniciar Etapa"
                  >
                    <RefreshCw size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* TIMELINE LIST */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
              <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                <ListTodo size={12} /> Roteiro de Mistura
              </div>

              <div className="relative border-l-2 border-slate-100 pl-4 ml-2.5 space-y-4">
                {activeRecipe.etapas.map((step, idx) => {
                  const isCurrent = idx === currentStepIdx && timerStatus !== "concluido";
                  const isCompleted = idx < currentStepIdx || timerStatus === "concluido";
                  
                  return (
                    <div key={step.id} className="relative">
                      {/* Circle indicator */}
                      <span
                        className={`absolute -left-[27px] top-1 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center text-[8px] font-bold transition-all ${
                          isCurrent
                            ? "bg-blue-600 border-blue-600 text-white ring-4 ring-blue-100 animate-pulse"
                            : isCompleted
                            ? "bg-emerald-500 border-emerald-500 text-white"
                            : "bg-white border-slate-300 text-slate-400"
                        }`}
                      >
                        {idx + 1}
                      </span>

                      <div className={`p-2.5 rounded-xl border transition-all ${
                        isCurrent 
                          ? "bg-blue-50 border-blue-200 shadow-sm" 
                          : "bg-white border-slate-200/70"
                      }`}>
                        <div className="flex justify-between items-center">
                          <span className={`text-xs font-bold ${isCurrent ? "text-blue-800" : isCompleted ? "text-slate-400 line-through" : "text-slate-700"}`}>
                            {step.nome}
                          </span>
                          <span className="text-[10px] font-bold text-slate-400 font-mono">
                            {step.minutos}m {step.segundos}s
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400">
            <AlertCircle size={36} className="mb-2 text-slate-300" />
            <span className="text-xs font-semibold">Nenhuma etapa cadastrada para este gesso.</span>
          </div>
        )}

        {/* CUSTOM GESSO MODAL */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] animate-scale-up">
              <div className="p-5 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800">Cadastrar Novo Gesso</h3>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
              </div>

              <form onSubmit={saveCustomRecipe} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Gesso *</label>
                  <input
                    type="text"
                    required
                    value={newRecipeName}
                    onChange={(e) => setNewRecipeName(e.target.value)}
                    placeholder="Ex: Optima Prestige Red"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Proporção Gesso (g)</label>
                    <input
                      type="number"
                      required
                      value={newPropGesso}
                      onChange={(e) => setNewPropGesso(e.target.value)}
                      className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-mono font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Proporção Água (ml)</label>
                    <input
                      type="number"
                      required
                      value={newPropAgua}
                      onChange={(e) => setNewPropAgua(e.target.value)}
                      className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-mono font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações</label>
                  <textarea
                    value={newObs}
                    onChange={(e) => setNewObs(e.target.value)}
                    placeholder="Opcional..."
                    rows={2}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                  />
                </div>

                {/* ETAPAS EDITOR */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">Etapas de Preparo</label>
                    <button
                      type="button"
                      onClick={handleAddStep}
                      className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                    >
                      + Adicionar Etapa
                    </button>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar border border-slate-100 p-2 rounded-lg bg-slate-50">
                    {newSteps.map((step, idx) => (
                      <div key={step.id} className="flex gap-2 items-center bg-white p-2 rounded-lg border border-slate-200/80 shadow-sm">
                        <span className="text-xs font-bold text-slate-400 w-5">{idx + 1}.</span>
                        <input
                          type="text"
                          required
                          value={step.nome}
                          onChange={(e) => handleStepChange(step.id, "nome", e.target.value)}
                          placeholder="Nome da etapa"
                          className="flex-1 p-1.5 border border-slate-300 rounded text-xs outline-none focus:border-blue-500 font-semibold"
                        />
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            value={step.minutos}
                            onChange={(e) => handleStepChange(step.id, "minutos", e.target.value)}
                            className="w-12 p-1.5 border border-slate-300 rounded text-xs font-mono font-bold text-center"
                            placeholder="Min"
                          />
                          <span className="text-[10px] text-slate-400 font-bold">m</span>
                          <input
                            type="number"
                            min="0"
                            max="59"
                            value={step.segundos}
                            onChange={(e) => handleStepChange(step.id, "segundos", e.target.value)}
                            className="w-12 p-1.5 border border-slate-300 rounded text-xs font-mono font-bold text-center"
                            placeholder="Seg"
                          />
                          <span className="text-[10px] text-slate-400 font-bold">s</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveStep(step.id)}
                          className="p-1.5 text-slate-400 hover:text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm"
                  >
                    Salvar Receita
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
